import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { Col } from 'react-bootstrap';
import { selectFerpaState } from '../../../../../features/authentication/authSlice';
import { useSelector } from 'react-redux';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import HesSpinner from '../../../../../components/loadingOverlay/hesSpinner';
import { useAssignmentResponseMutation, useSessionResponseMutation } from '../../../../../features/authentication/reportApiSlice';
import { useOverrideResponseMutation } from '../../../../../features/authentication/assignmentApislice';
import CustomToast from '../../../../../components/Toast';
import { DateFormatter } from '../../../../../utils/DateFormatter';
import ProgressButton from '../../../../../components/ProgressButton';
import FERPA from '../../../../../components/FERPA/FerpaComponent';
import { ActivityUtils } from '../../../../activitySearch/activityUtils';

const Responses = () => {
  const { assignmentId, activityType, studentId, sessionId, isSelfStudy } = useParams();
  const [assignmentResponse] = useAssignmentResponseMutation();
  const [sessionResponse] = useSessionResponseMutation();
  const [overrideResponse] = useOverrideResponseMutation();
  const [assignmentResponseData, setAssignmentResponseData] = React.useState();
  const [selectedQuestionId, setSelectedQuestionId] = React.useState();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const ferpaEnabled = useSelector(selectFerpaState);
  const [loader, setLoader] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState();
  const [isPdfGenerating, setIsPdfGenerating] = React.useState(false);
  const ferpaRef = useRef(null);

  React.useEffect(() => {
    fetchAssignmentResponse();
  }, []);

  const fetchAssignmentResponse = async () => {
    const data = {studentId, ...(isSelfStudy === 'true' ? { sessionId: sessionId } : { assignmentId: assignmentId })};
    const apiCall = isSelfStudy === 'true' ? sessionResponse : assignmentResponse;

    try {
      const response = await apiCall(data).unwrap();
      setAssignmentResponseData(response);
    } catch (error) {
      navigate(-1);
      CustomToast(error.data, 'error');
    }
  };

  const handlePrint = async () => {
    if(isPdfGenerating) return;

    setIsPdfGenerating(true);

    const contentWrapper = document.getElementById('pdf_Wrapper');
    const contentClone = contentWrapper.cloneNode(true);
    const tables = document.getElementsByTagName('table');
    const pTag = document.getElementsByTagName('p');
    const studentName = contentClone.querySelector('#student-name');
    studentName.classList.add('d-none');
    const buttonGroup = contentClone.querySelector('#buttonGroup');
    buttonGroup.classList.add('d-none');
    const readingSelections = contentClone.querySelectorAll('#rs_description');
    readingSelections.forEach(el => el.classList.remove('d-none'));

    for (let i = 0; i < tables.length; i++) {
      const table = tables[i];
      table.style.pageBreakInside = 'avoid';
      table.style.breakInside = 'avoid';
      table.style.keepWithWindow = 'always';
    }

    for (let i = 0; i < pTag.length; i++) {
      const p = pTag[i];
      p.style.pageBreakInside = 'avoid';
      p.style.breakInside = 'avoid';
      p.style.keepWithWindow = 'always';
    }

    const options = {
      margin: 60,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
      pagebreak: {
        mode: ['avoid-all', 'css', 'legacy']
      }
    };

    await html2pdf().set(options).from(contentClone).save('Responses.pdf');
    setIsPdfGenerating(false);
  };

  const handleRadioChange = (choice, question, index) => {
    setSelectedIndex(index);
    setLoader(true);
    const updatedQuestion = {
      ...question,
      studentResponse: {
        ...question.studentResponse,
        response: choice,
        teacherOverride:
          choice === 'Choice 0' ? 0 : choice === 'Choice 1' ? 1 : choice === 'Choice 2' ? 2 : null
      }
    };

    setAssignmentResponseData((prevData) => {
      const updatedQuestions = prevData.questions.map((q) => {
        if (q.questionOrder === updatedQuestion.questionOrder) {
          return updatedQuestion;
        }
        return q;
      });

      return { ...prevData, questions: updatedQuestions };
    });

    const data = {
      assignmentId: assignmentId,
      questionIndex: index + 1,
      sudentId: studentId,
      option:
        choice === 'Choice 0' ? 0 : choice === 'Choice 1' ? 1 : choice === 'Choice 2' ? 2 : null
    };

    overrideResponse(data)
      .then((response) => {
        setAssignmentResponseData((prevData) => {
          const updatedQuestions = prevData.questions?.map((q) => {
            if (q.questionOrder === updatedQuestion?.questionOrder) {
              return {
                ...q,
                studentResponse: {
                  ...q.studentResponse,
                  response: response.data?.response,
                  retryResponse: response.data?.retryResponse,
                  status: response.data?.status
                }
              };
            }
            return q;
          });

          return { ...prevData, questions: updatedQuestions };
        });
        setLoader(false);
        setSelectedIndex(null);
      })
      .catch((error) => {
        console.error('Error updating API response:', error);
      });
  };

  const handleToggle = (index) => {
    setIsExpanded(index == selectedQuestionId ? !isExpanded : true);
    setSelectedQuestionId(index);
  };

  return assignmentResponseData ? (
    <div className="content-wrapper" id="pdf_Wrapper">
      <div className="white-card assignment-response-header mb-4">
        <div className='d-flex flex-row justify-content-between  mb-4'>
          <h3>Responses Report</h3>
          <div id="buttonGroup" className="d-flex justify-content-end gap-3">
            <div className="d-flex me-3 align-items-center">
              <FERPA ref={ferpaRef} />
            </div>
            <button className="btn btn-width btn-castleWhite" id="back_button" onClick={() => navigate(-1)}>
              Back
            </button>
            <ProgressButton
              isLoading={isPdfGenerating}
              onClick={handlePrint}
              loadingLabel="Generating PDF"
              label="Print to PDF"
              className="btn btn-castleRed"
              expectedDuration={1500}
            />
          </div>
        </div>

        <div className="row justify-content-between">
          <div className={`${isSelfStudy === 'true' ? 'col-lg-8' : 'col-lg-4'} d-flex flex-wrap flex-column flex-sm-row flex-lg-column justify-content-between justify-content-lg-start gap-1 mb-2 mb-lg-0 text-center text-sm-start`}>
            <p>
              Student:{' '}
              <strong className='ms-2'>
                {`${ferpaEnabled ? assignmentResponseData.student : 'name hidden'}`}
              </strong>
              <button
                id="student-name"
                className="btn-link primary-link ms-2 "
                onClick={() => ferpaRef?.current?.setFerpaState(!ferpaEnabled)}>
                <FontAwesomeIcon icon={ferpaEnabled ? faEyeSlash : faEye} />{' '}
                {ferpaEnabled ? 'Hide Name' : 'Show Name'}
              </button>
            </p>
            <p>
              Description:<strong> {assignmentResponseData.description}</strong>
            </p>
          </div>
          {isSelfStudy !== 'true' && 
          <div className="col-lg-4 text-center d-flex flex-wrap flex-column flex-sm-row flex-lg-column justify-content-between justify-content-lg-start gap-1 mb-2 mb-lg-0">
            <p>
              Date Assigned:<strong> {assignmentResponseData.dateAssigned.replace(/\//g, '-')}</strong>
            </p>
            <p>
              Date Completed:<strong> {assignmentResponseData.dateCompleted.replace(/\//g, '-')} </strong>
            </p>
          </div>
          }
          <div className="col-lg-4 text-center text-sm-end d-flex flex-wrap flex-column flex-sm-row flex-lg-column justify-content-between justify-content-lg-start gap-1">
            <p>
              {isSelfStudy ? 'Session Created:' : `${ActivityUtils.getActivityName(activityType)} Mode:`}<strong> {isSelfStudy ? assignmentResponseData?.sessionCreatedOn?.replace(/\//g, '-') : assignmentResponseData?.assignmentMode}</strong>
            </p>
            <p>
              Report Date:<strong> {DateFormatter.toLongDateTime2(assignmentResponseData?.reportDate)}</strong>
            </p>
          </div>
        </div>
        <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center assignment-response-conunt pt-3 mt-3 gap-2 text-center">
          <p>
            Number of questions in the {ActivityUtils.getActivityName(activityType).toLowerCase()}{' '}
            <span>{assignmentResponseData.questionCount}</span>
          </p>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            <p>
              % Correct{' '}
              <span className="correct">{`${assignmentResponseData.correctAnswerPercentage}%`}</span>
            </p>
            <p>
              Number of correct answers <span>{assignmentResponseData.numberOfCorrectAnswer}</span>
            </p>
          </div>
        </div>
      </div>

      {assignmentResponseData.directions && (
        <div className="white-card">
          <div className="w-100  bg-light p-2">
            <h2 className="fw-bold mb-auto">Direction</h2>
          </div>
          <div dangerouslySetInnerHTML={{ __html: assignmentResponseData.directions }}></div>
        </div>
      )}

      {assignmentResponseData.questionBar && (
        <div className="white-card">
          <div className="w-100  bg-light p-2">
            <h2 className="fw-bold mb-auto">Question</h2>
          </div>
          <div dangerouslySetInnerHTML={{ __html: assignmentResponseData.readingSelections.join('') }}></div>
        </div>
      )}

      {assignmentResponseData.readingSelections?.length > 0 && (
        <div className="white-card">
          <div className="w-100  bg-light p-2">
            <h2 className="fw-bold mb-auto">Reading Selection</h2>
          </div>
          <div dangerouslySetInnerHTML={{ __html: assignmentResponseData.readingSelections.join('') }}></div>
        </div>
      )}

      {assignmentResponseData.questions.map((question, mainIndex) => {
        const decodedHtml = decodeURIComponent(question?.questionEx?.crAnswer);
        return (
          <div className="white-card avoid-split" key={mainIndex}>
            <div className="assignment-response-qa mb-4 pb-2">
              <div className="d-flex">
                <div className='d-flex flex-row gap align-content-center me-2'>
                  <h6 className="flex-shrink-0 me-2">{mainIndex + 1}.</h6>
                  {question.mcAnswer && <span style={{ marginTop: '4px' }}><u>&nbsp;&nbsp;{question.mcAnswer}&nbsp;&nbsp;</u></span>}
                </div>

                <div className='d-flex flex-column'>
                  {question.graphicInfo.map((graphicInfo, index) => {
                    return (
                      <>
                        <div className="d-flex flex-row align-items-center" key={index}>
                          {graphicInfo.type == '.htm' && (
                            <>
                              <div className='d-flex flex-row align-items-center' dangerouslySetInnerHTML={{__html: graphicInfo.selectionTitle}}></div>

                              {graphicInfo.selectionTitle && <button
                                style={{
                                  background: '#e1eeff',
                                  color: 'rgba(84, 113, 200, 0.6)',
                                  width: '30px',
                                  height: '30px',
                                  textAlign: 'center',
                                  border: 'none',
                                  marginLeft: '10px'
                                }}
                                className="rounded-circle"
                                onClick={() => handleToggle(mainIndex)}>
                                {isExpanded && selectedQuestionId == mainIndex ? (
                                  <FontAwesomeIcon icon={faChevronUp} />
                                ) : (
                                  <FontAwesomeIcon icon={faChevronDown} />
                                )}
                              </button>}
                            </>
                          )}
                          <div></div>
                        </div>
                        <div id='rs_description' className={`${(isExpanded && selectedQuestionId == mainIndex) ? '' : 'd-none'}`} dangerouslySetInnerHTML={{ __html: graphicInfo.description }}></div>
                      </>
                    );
                  })}
                  <p dangerouslySetInnerHTML={{ __html: question.questionText }}></p>
                  {question.isBlocked && (
                    <p className="text-danger">
                      <strong
                        dangerouslySetInnerHTML={{
                          __html: question.blockedQuestionMessage
                        }}></strong>
                    </p>
                  )}
                </div>
              </div>

              {question.questionEx && (
                <div className="assignment-response-answer avoid-split">
                  <Col className="avoid-split" md={6}>
                    <p>
                      Status: <span className={`${question.questionEx.crStatus == 'Answered' ? 'correct' : question.questionEx.crStatus == 'Not Answered' ? 'incorrect' : 'inprogress'}`}>
                        <strong>{question.questionEx.crStatus}</strong>
                      </span>
                    </p>
                    <p>
                      Answer: <strong dangerouslySetInnerHTML={{ __html: decodedHtml }}></strong>
                    </p>
                    <p>
                      Points: <strong>{question.questionEx.crPoints}</strong>
                    </p>
                  </Col>
                </div>
              )}
              {question.studentResponse.isTeacherOverride ? (
                <div className="assignment-response-answer avoid-split">
                  {loader && selectedIndex === mainIndex ? (
                    <div
                      className="d-flex justify-content-start align-items-center"
                      style={{ height: '40px' }}>
                      <HesSpinner />
                      <span className="ms-3">Loading results...</span>
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-3" id="status-bar2">
                      <p>
                        Status{' '}
                        <span
                          className={
                            question.studentResponse.status === 'Correct on Retry' ||
                              question.studentResponse.status === 'Correct'
                              ? 'correct'
                              : 'incorrect'
                          }>
                          {question.studentResponse.status}
                        </span>
                      </p>
                      {question.studentResponse.response && (
                        <p>
                          Response: <strong>{question.studentResponse.response}</strong>
                        </p>
                      )}
                      {question.studentResponse.retryResponse && (
                        <p>
                          Retry Response:{' '}
                          <strong>{question?.studentResponse?.retryResponse}</strong>
                        </p>
                      )}
                    </div>
                  )}
                  {assignmentResponseData?.showTeacherOverride &&
                  <div id="status-bar1">
                    <div className="d-flex flex-wrap gap-3 align-items-center mt-3">
                      <label className="form-label mb-0 me-2">Teacher Override</label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name={`flexRadioDefault${mainIndex}`}
                          id="flexRadioDefault3"
                          checked={question.studentResponse.teacherOverride === 0}
                          onClick={() => handleRadioChange('Choice 0', question, mainIndex)}
                        />
                        <label className="form-check-label ms-2" htmlFor="flexRadioDefault3">
                          None
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name={`flexRadioDefault${mainIndex}`}
                          id="flexRadioDefault4"
                          checked={question.studentResponse.teacherOverride === 1}
                          onClick={() => handleRadioChange('Choice 1', question, mainIndex)}
                        />
                        <label className="form-check-label ms-2" htmlFor="flexRadioDefault4">
                          First Answer
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name={`flexRadioDefault${mainIndex}`}
                          id="flexRadioDefault5"
                          checked={question.studentResponse.teacherOverride === 2}
                          onClick={() => handleRadioChange('Choice 2', question, mainIndex)}
                        />
                        <label className="form-check-label ms-2" htmlFor="flexRadioDefault5">
                          Retry Answer
                        </label>
                      </div>
                    </div>
                  </div>
                  }
                </div>
              ) : (
                !question.isBlocked &&
                !question.questionEx && (
                  <div id="status-bar" className="assignment-response-answer avoid-split">
                    <div className="d-flex flex-wrap gap-3">
                      <p>
                        Status{' '}
                        <span
                          className={
                            (question.questionEx
                              ? question.questionEx?.crStatus
                              : question.studentResponse?.status) === 'Correct on Retry' ||
                              (question.questionEx
                                ? question.questionEx?.crStatus
                                : question.studentResponse?.status) === 'Correct'
                              ? 'correct'
                              : 'incorrect'
                          }>
                          <strong>
                            {question.questionEx
                              ? question.questionEx?.crStatus
                              : question.studentResponse?.status}
                          </strong>
                        </span>

                        {question.studentResponse.response && (
                          <p>
                            Response: <strong>{question.studentResponse.response}</strong>
                          </p>
                        )}
                        {question.studentResponse.retryResponse && (
                          <p>
                            Retry Response:{' '}
                            <strong>{question?.studentResponse?.retryResponse}</strong>
                          </p>
                        )}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '40px' }}>
      <HesSpinner />
      <span className="ms-3">Loading results...</span>
    </div>
  );
};

export default Responses;
